// SCR-310: the Assistant's sessions for one org. A table of conversations (started, by whom, state,
// changes and spend only when the API sends them), New conversation, and, with none yet, the
// starter requests the principal's read tools answer. A starter opens a new conversation with its
// text in the composer. No session id is shown: a row is named by when and by whom (AS-1). On the
// proxy's Admin area (NAV-025) no spend is ever shown, a session AllCode's staff started is "AllCode
// support", and keys, people and invites are pointed to the People and Keys sections. A session
// the operator's Claude Code drives (`channel = mcp`) carries a "Claude Code" chip.
import { useEffect, useState } from "preact/hooks";
import { ApiError } from "../../api/errors";
import { useHost, type Host } from "../../host/host";
import { HelpButton } from "../../primitives/help";
import { Link } from "../../primitives/link";
import { Chip, PageHeader } from "../../primitives/page";
import { Loading } from "../../primitives/states";
import { DataTable, Money, type Column } from "../../primitives/table";
import { useScopeMe } from "../../scope/scope";
import { AssistantError } from "./refusal";
import {
  changesLabel,
  CLAUDE_CODE_LABEL,
  drivenFromClaudeCode,
  operatorLabel,
  sessionTitle,
  showsChanges,
  showsSpend,
  startersFor,
  STATE_LABELS,
  STATE_TONES,
  when,
  type AgentSession,
  type Starter,
} from "./model";
import { useAssistantTransport } from "./transport";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

/** The query key a starter's text travels in to the new conversation. */
export const STARTER_PARAM = "ask";

/** How the proxy names a session AllCode's staff started: never a staff member's name. */
export const ALLCODE_SUPPORT = "AllCode support";

/** Whether the shell is the proxy's Admin area (the customer side). */
export function onProxy(host: Pick<Host, "kind">): boolean {
  return host.kind === "proxy-admin-area";
}

/** The surface whose sessions this shell continues: the proxy's are the customer's, the AllCode admin's staff's. */
export function shellSurface(host: Pick<Host, "kind">): AgentSession["surface"] {
  return onProxy(host) ? "customer" : "staff";
}

/** Whether this shell may send to the session: the API refuses every message from the other surface (N5). */
export function continuesHere(host: Pick<Host, "kind">, session: Pick<AgentSession, "surface">): boolean {
  return session.surface === shellSurface(host);
}

/** Who started the session, as this shell shows it: on the proxy a staff session is AllCode support. */
export function startedBy(host: Pick<Host, "kind">, session: AgentSession): string {
  return onProxy(host) && session.surface === "staff" ? ALLCODE_SUPPORT : operatorLabel(session);
}

/** Whether the shell shows model spend: the AllCode admin when the API sends it, the proxy never. */
export function spendShown(host: Pick<Host, "kind">, sessions: readonly AgentSession[]): boolean {
  return !onProxy(host) && showsSpend(sessions);
}

/** "Claude Code" beside a session its operator's Claude Code drives (M3); nothing beside a web session. */
export function ChannelChip({ session }: { session: Pick<AgentSession, "channel"> }) {
  if (!drivenFromClaudeCode(session)) return null;
  return (
    <>
      {" "}
      <Chip tone="grey">{CLAUDE_CODE_LABEL}</Chip>
    </>
  );
}

export interface AssistantSessionsProps {
  /** The conversation's href in the mounting shell, for a session id. */
  sessionHref(sessionId: string): string;
}

export function AssistantSessions({ sessionHref }: AssistantSessionsProps) {
  const transport = useAssistantTransport();
  const host = useHost();
  const me = useScopeMe();
  const [sessions, setSessions] = useState<AgentSession[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<ApiError | null>(null);

  useEffect(() => {
    let current = true;
    setSessions(null);
    setError(null);
    transport
      .list()
      .then((items) => current && setSessions(items))
      .catch((err: unknown) => current && setError(unexpected(err)));
    return () => {
      current = false;
    };
  }, [transport, attempt]);

  const start = (starter?: Starter) => {
    setCreating(true);
    setCreateError(null);
    transport
      .create()
      .then((session) => {
        const href = sessionHref(session.id);
        host.navigate(starter ? `${href}?${new URLSearchParams({ [STARTER_PARAM]: starter.text }).toString()}` : href);
      })
      .catch((err: unknown) => setCreateError(unexpected(err)))
      .finally(() => setCreating(false));
  };

  const header = (
    <PageHeader
      title="Assistant"
      lede={onProxy(host) ? <ProxyLede basePath={host.basePath} /> : "Ask about this customer's usage, quotas, people, teams and settings. Answers name people and teams, never ids."}
      actions={
        <button type="button" class="button button-primary" disabled={creating} onClick={() => start()}>
          {creating ? "Starting…" : "New conversation"}
        </button>
      }
    />
  );

  return (
    <section class="assistant assistant-sessions" aria-label="Assistant">
      {header}
      {createError && <AssistantError error={createError} />}
      {error ? (
        <AssistantError error={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : sessions === null ? (
        <Loading label="Loading conversations…" />
      ) : sessions.length === 0 ? (
        <Starters starters={startersFor(me)} disabled={creating} onPick={start} />
      ) : (
        <SessionTable sessions={sessions} sessionHref={sessionHref} host={host} />
      )}
    </section>
  );
}

/** The proxy's lede: what it can do, and where keys, people and invites are managed instead (Q3). */
function ProxyLede({ basePath }: { basePath: string }) {
  return (
    <>
      Ask about your organisation's usage, quotas, teams and settings, and have it propose changes you approve. Keys, people and invites are managed in{" "}
      <Link href={`${basePath}/people`}>People</Link> and <Link href={`${basePath}/keys`}>Keys</Link>. <HelpButton topic="assistant.approvals" label="Approving a change" />
      <HelpButton topic="assistant.whoPays" label="Who pays for the Assistant" />
    </>
  );
}

function Starters({ starters, disabled, onPick }: { starters: readonly Starter[]; disabled: boolean; onPick(starter: Starter): void }) {
  return (
    <section class="card assistant-empty" aria-labelledby="assistant-empty-title">
      <h2 id="assistant-empty-title">Ask about models, sign-in, quotas or usage</h2>
      <p class="muted">No conversations yet. Start one, or pick a question:</p>
      {starters.length > 0 && (
        <ul class="assistant-starters">
          {starters.map((starter) => (
            <li key={starter.tool}>
              <button type="button" class="button button-secondary assistant-starter" disabled={disabled} onClick={() => onPick(starter)}>
                {starter.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SessionTable({ sessions, sessionHref, host }: { sessions: readonly AgentSession[]; sessionHref(sessionId: string): string; host: Host }) {
  const columns: Column<AgentSession>[] = [
    {
      key: "started",
      header: "Started",
      cell: (s) => (
        <Link href={sessionHref(s.id)} aria-label={onProxy(host) ? `Conversation started ${when(s.createdAt)} by ${startedBy(host, s)}` : sessionTitle(s)}>
          {when(s.createdAt)}
        </Link>
      ),
      compare: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
    { key: "operator", header: "Started by", cell: (s) => (
        <>
          {startedBy(host, s)}
          <ChannelChip session={s} />
        </>
      ),
    },
    { key: "installation", header: "Installation", cell: (s) => s.installationName ?? "—" },
    { key: "state", header: "State", cell: (s) => <Chip tone={STATE_TONES[s.state]}>{STATE_LABELS[s.state]}</Chip> },
    { key: "turns", header: "Turns", numeric: true, cell: (s) => s.turnsUsed },
  ];
  if (showsChanges(sessions)) columns.push({ key: "changes", header: "Changes", cell: (s) => changesLabel(s) ?? "—" });
  if (spendShown(host, sessions)) {
    columns.push({ key: "spend", header: "Model spend", numeric: true, cell: (s) => (s.spentMicroUsd === null ? "—" : <Money micro={s.spentMicroUsd} />) });
  }
  return <DataTable caption="Conversations" hideCaption columns={columns} rows={sessions} rowKey={(s) => s.id} emptyMessage="No conversations yet." />;
}
